import React, { useState } from 'react';
import type { Venue } from '../../../types/venue';
import { updateVenue } from '../../../utils/venueApi';
import Button from '../../ui/Button';

interface ReviewSettingsPanelProps {
  venue: Venue;
  onUpdate: (venue: Venue) => void;
}

const inputBase =
  'w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const ReviewSettingsPanel: React.FC<ReviewSettingsPanelProps> = ({ venue, onUpdate }) => {
  const [enabled, setEnabled] = useState(venue.review?.enabled ?? false);
  const [googleUrl, setGoogleUrl] = useState(venue.review?.googleUrl ?? '');
  const [yelpUrl, setYelpUrl] = useState(venue.review?.yelpUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const updated = await updateVenue(venue.id, {
        review: { enabled, googleUrl, yelpUrl },
      } as any);
      onUpdate(updated);
      setFeedback({ type: 'success', message: 'Review settings saved.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Reviews</div>
        <h2 className="text-display text-2xl text-coffee-900">Review funnel</h2>
      </div>

      <label className="flex items-center gap-3 p-4 rounded-xl border border-coffee-100 bg-white cursor-pointer hover:border-coffee-200 transition-colors">
        <div
          role="switch"
          aria-checked={enabled}
          tabIndex={0}
          onClick={() => setEnabled(!enabled)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEnabled(!enabled); } }}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            enabled ? 'bg-coffee-600' : 'bg-coffee-200'
          }`}
        >
          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
            enabled ? 'translate-x-5' : ''
          }`} />
        </div>
        <div>
          <span className="text-sm font-medium text-coffee-900">Enable review prompts</span>
          <p className="text-xs text-coffee-500 mt-0.5">
            First-time guests will see a prompt to leave a review after connecting.
          </p>
        </div>
      </label>

      {enabled && (
        <div className="bg-white border border-coffee-100 rounded-2xl p-6 space-y-4">
          <div>
            <label htmlFor="google-url" className="block text-sm font-medium text-coffee-800 mb-1.5">
              Google Reviews URL
            </label>
            <input
              id="google-url"
              type="url"
              value={googleUrl}
              onChange={(e) => setGoogleUrl(e.target.value)}
              placeholder="https://g.page/your-business/review"
              className={inputBase}
            />
          </div>
          <div>
            <label htmlFor="yelp-url" className="block text-sm font-medium text-coffee-800 mb-1.5">
              Yelp URL
            </label>
            <input
              id="yelp-url"
              type="url"
              value={yelpUrl}
              onChange={(e) => setYelpUrl(e.target.value)}
              placeholder="https://yelp.com/biz/your-business"
              className={inputBase}
            />
          </div>
        </div>
      )}

      {feedback && (
        <div className={`text-sm rounded-xl p-4 border ${
          feedback.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      <Button variant="primary" onClick={handleSave} loading={saving}>
        {saving ? 'Saving...' : 'Save settings'}
      </Button>
    </div>
  );
};

export default ReviewSettingsPanel;
